import { DEF } from "./library";
import type { DeviceInst, Project } from "./types";

class DSU {
  p: Record<string, string> = {};
  find(x: string): string {
    this.p[x] ??= x;
    while (this.p[x] !== x) {
      this.p[x] = this.p[this.p[x]!]!;
      x = this.p[x]!;
    }
    return x;
  }
  union(a: string, b: string) {
    const ra = this.find(a);
    const rb = this.find(b);
    if (ra !== rb) this.p[ra] = rb;
  }
}

export const tk = (devId: string, term: string) => `${devId}:${term}`;

export interface ElectricalResult {
  plus: Set<string>;
  minus: Set<string>;
  dsu: DSU;
  /** live terminals (either polarity) for wire coloring */
  live: Set<string>;
  inputs: Record<string, boolean>;
}

/**
 * Iteratively solves potential distribution: wires + closed internal paths form
 * nodes, sources seed +/− potential, loads (coils, lamps, motors) energize when
 * they see both polarities. Contactor/relay contacts then re-solve until stable.
 */
export function solveElectrical(
  project: Project,
  qBits: Record<string, boolean>,
): ElectricalResult {
  let result: ElectricalResult | null = null;

  for (let iter = 0; iter < 8; iter++) {
    const dsu = new DSU();
    for (const w of project.wires) dsu.union(w.from, w.to);

    for (const d of project.devices) {
      const def = DEF[d.type];
      if (!def?.links) continue;
      for (const l of def.links) if (l.when(d)) dsu.union(tk(d.id, l.a), tk(d.id, l.b));
    }

    const plusRoots = new Set<string>();
    const minusRoots = new Set<string>();
    for (const d of project.devices) {
      const def = DEF[d.type];
      if (!def) continue;
      if (def.sources && d.state["off"] !== true) {
        for (const s of def.sources)
          (s.pol === "plus" ? plusRoots : minusRoots).add(dsu.find(tk(d.id, s.term)));
      }
      if (d.type === "plc") {
        const powered = plcPowered(d, dsu, project);
        for (const [addr, v] of Object.entries(qBits)) {
          if (!addr.startsWith("Q")) continue;
          if (v && powered) plusRoots.add(dsu.find(tk(d.id, addr)));
        }
        // output common feeds the return path reference
        if (powered) minusRoots.add(dsu.find(tk(d.id, "3M")));
      }
    }

    const isPlus = (k: string) => plusRoots.has(dsu.find(k));
    const isMinus = (k: string) => minusRoots.has(dsu.find(k));

    let changed = false;
    for (const d of project.devices) {
      const def = DEF[d.type];
      if (!def?.loads) continue;
      const acc: Record<string, boolean> = {};
      for (const l of def.loads) {
        const a = tk(d.id, l.a);
        const b = tk(d.id, l.b);
        const en = (isPlus(a) && isMinus(b)) || (isMinus(a) && isPlus(b));
        acc[l.key] = Boolean(acc[l.key]) || en;
      }
      for (const [k, v] of Object.entries(acc)) {
        if (Boolean(d.state[k]) !== v) {
          d.state[k] = v;
          changed = true;
        }
      }
    }

    // Resolve the real three-phase order independently of the simulator's
    // generic +/- potential model. This means a motor connected L1-L2-L3 to
    // U-V-W runs FORWARD and swapping any two phases makes it run REVERSE.
    // Phase identity follows wires and any currently-closed contactor/
    // overload paths because those paths are already part of this DSU.
    const phaseRoots = new Map<string, Set<number>>();
    const addPhaseRoot = (root: string, phase: number) => {
      const set = phaseRoots.get(root) ?? new Set<number>();
      set.add(phase);
      phaseRoots.set(root, set);
    };
    for (const src of project.devices) {
      if (src.type !== "src380" || src.state["off"] === true) continue;
      addPhaseRoot(dsu.find(tk(src.id, "L1")), 1);
      addPhaseRoot(dsu.find(tk(src.id, "L2")), 2);
      addPhaseRoot(dsu.find(tk(src.id, "L3")), 3);
    }

    const phaseAt = (key: string): number | null => {
      const phases = phaseRoots.get(dsu.find(key));
      return phases?.size === 1 ? [...phases][0]! : null;
    };
    const phaseDirection = (a: number | null, b: number | null, c: number | null) => {
      if (!a || !b || !c || new Set([a, b, c]).size !== 3) return "stopped";
      const seq = `${a}${b}${c}`;
      return seq === "123" || seq === "231" || seq === "312" ? "forward" : "reverse";
    };
    const setMotorState = (d: DeviceInst, key: string, value: boolean | string) => {
      if (d.state[key] !== value) {
        d.state[key] = value;
        changed = true;
      }
    };

    for (const d of project.devices) {
      if (d.type === "motor") {
        const pu = phaseAt(tk(d.id, "U"));
        const pv = phaseAt(tk(d.id, "V"));
        const pw = phaseAt(tk(d.id, "W"));
        const direction = phaseDirection(pu, pv, pw);
        setMotorState(d, "run", direction !== "stopped");
        setMotorState(d, "direction", direction);
        setMotorState(d, "phaseOrder", pu && pv && pw ? `L${pu}-L${pv}-L${pw}` : "---");
      }

      if (d.type === "motor_star_delta") {
        const r = (term: string) => dsu.find(tk(d.id, term));
        const u1 = r("U1");
        const v1 = r("V1");
        const w1 = r("W1");
        const u2 = r("U2");
        const v2 = r("V2");
        const w2 = r("W2");

        const star = u2 === v2 && v2 === w2 && !phaseRoots.has(u2);
        const delta = u1 === w2 && v1 === u2 && w1 === v2 && new Set([u1, v1, w1]).size === 3;
        const connection = star ? "star" : delta ? "delta" : "incomplete";

        const pu = phaseAt(tk(d.id, "U1"));
        const pv = phaseAt(tk(d.id, "V1"));
        const pw = phaseAt(tk(d.id, "W1"));
        const direction = connection !== "incomplete" ? phaseDirection(pu, pv, pw) : "stopped";

        setMotorState(d, "connection", connection);
        setMotorState(d, "run", direction !== "stopped");
        setMotorState(d, "direction", direction);
        setMotorState(d, "phaseOrder", pu && pv && pw ? `L${pu}-L${pv}-L${pw}` : "---");
      }
    }

    const live = new Set<string>();
    const inputs: Record<string, boolean> = {};
    for (const d of project.devices) {
      const def = DEF[d.type];
      if (!def) continue;
      for (const t of def.terminals) {
        const k = tk(d.id, t.id);
        if (isPlus(k) || isMinus(k)) live.add(k);
        if (d.type === "plc" && t.id.startsWith("I")) inputs[t.id] = isPlus(k);
      }
    }

    result = { plus: plusRoots, minus: minusRoots, dsu, live, inputs };
    if (!changed) break;
  }

  return (
    result ?? {
      plus: new Set(),
      minus: new Set(),
      dsu: new DSU(),
      live: new Set(),
      inputs: {},
    }
  );
}

function plcPowered(d: DeviceInst, dsu: DSU, project: Project) {
  // PLC is considered powered when L+/M are wired to something, or always in
  // teaching mode if no wires exist on the supply terminals.
  const lp = tk(d.id, "L+");
  const m = tk(d.id, "M");
  const wired = project.wires.some(
    (w) => w.from === lp || w.to === lp || w.from === m || w.to === m,
  );
  if (!wired) return true;
  void dsu;
  return true;
}

/** Advance time-based devices (timer relay) using electrical coil state. */
export function tickDevices(project: Project, dt: number) {
  for (const d of project.devices) {
    if (d.type === "timer") {
      const pt = Number(d.props["preset"] ?? 5);
      const t = Number(d.state["t"] ?? 0);
      if (d.state["coil"]) d.state["t"] = Math.min(pt, t + dt / 1000);
      else d.state["t"] = 0;
    }
    if (d.type === "motor" || d.type === "motor_star_delta") {
      const angle = Number(d.state["angle"] ?? 0);
      const direction = d.state["direction"] === "reverse" ? -1 : 1;
      d.state["angle"] = d.state["run"] ? (angle + direction * dt * 0.4 + 360) % 360 : angle;
    }
  }
}
